import { forwardRef, memo, useCallback, useMemo, useRef, useState, type MouseEvent } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { HoverHelp } from '@/tableControls'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import type { PickerDriverType } from '@/store/projectionsStore'
import { daysInMonth, monthKeyFromT } from './engine/calendar'
import { impliedDriverInput, type DriverContext } from './engine/drivers'
import { selectableRefs } from './engine/dag'
import { allowedDrivers } from './engine/schema'
import type { CaseInputs, DriverParams, History, Model, SchemaEntry, View } from './engine/types'
import GridCell, { formatCompareDelta, HistoryValueCell, ReadOnlyValueCell } from './GridCell'
import type { ProjectionsGridCompare } from './ProjectionsGrid'
import type { CompareCell } from './compare/compareModel'
import {
  BARE_TRIGGER,
  GROUP_HEADER,
  GROUP_HEADER_GREY,
  GROUP_ROW,
  GROUP_STICKY_DRIVER,
  GROUP_STICKY_DRIVER_GREY,
  GROUP_STICKY_LABEL,
  GROUP_STICKY_LABEL_GREY,
  DRIVER_LABELS,
  GREEN_STATUS_TAG,
  GREYED,
  HIST_COL,
  INPUT_ROW,
  LEDGER_RULE_KEYS,
  LOCKED_DRIVER_SELECT,
  PROJ_EDGE,
  RED_STATUS_TAG,
  ROW,
  RULE_SUBTOTAL,
  RULE_TOTAL_BOTTOM,
  ROW_RULE_TOP,
  SECTION_BAND,
  SECTION_START_KEYS,
  SPACER_ROW,
  STICKY_DRIVER,
  STICKY_LABEL,
  VALUE,
  indentStyle,
  type GridRowModel,
  type PickerDriver,
} from './gridClasses'
import { cashTieBalanced, cashTieValues } from './cashTieCheck'
import { resolvePartnerLineLabel } from './partnerLineLabel'
import { recipeSubline } from './caseJson/recipe'
import { detectShape } from './caseJson/shape'
import { driverInputUnit, isDenomZero, isIdentity, orderedPickerDrivers } from './driverUtils'
import { explainCell } from './explain/explainCell'
import { buildLaborBuildupHelp, isLaborBuildupWagesRow } from './laborBuildupHelp'
import { formatByUnit, formatCurrency, formatDate, formatPercent } from './projectionsFormat'
import { historicalCellKey, labelCellKey, methodologyCellKey, projectedCellKey } from './cellKeys'
import DriverNoteIcon from './DriverNoteIcon'
import PayDatesPopover, { payPeriodParamsFromDriver } from './PayDatesPopover'
import SCurveModal from './SCurveModal'
import { distributionsInMonth } from './engine/cashPolicy'

const WEIGHT_LABEL: Record<string, string> = {
  fixed: '!text-sm !font-normal !text-gray-700',
  subtotal: '!text-sm !font-medium !text-gray-900',
  total: '!text-sm !font-semibold !text-gray-900',
  metric: '!text-sm !font-normal !italic !text-gray-600',
  derived: '!text-sm !font-normal !text-gray-600',
}

const WEIGHT_VALUE: Record<string, string> = {
  fixed: VALUE,
  subtotal: `${VALUE} font-medium`,
  total: `${VALUE} font-semibold`,
  metric: `${VALUE} italic text-gray-600`,
  derived: `${VALUE} text-gray-600`,
}

function computedRowClasses(row: Extract<GridRowModel, { kind: 'computed' }>): {
  trClass: string
  cellRule: string
  labelClass: string
  valueClass: string
} {
  const ledgerRule = LEDGER_RULE_KEYS.has(row.key)
  const showTopRule =
    !row.suppressTopRule &&
    (ledgerRule || row.weight === 'subtotal' || row.weight === 'total')
  const bottomRule =
    row.key === 'is.net_income_loss'
      ? ''
      : row.weight === 'total'
        ? RULE_TOTAL_BOTTOM
        : ''
  const ledgerWeight = ledgerRule ? '!font-semibold !text-gray-900' : ''
  return {
    trClass: showTopRule ? ROW_RULE_TOP : '',
    cellRule: bottomRule,
    labelClass: ledgerWeight,
    valueClass: ledgerRule ? 'font-semibold' : '',
  }
}

export type OpenNoteOptions = {
  variant?: 'cell' | 'line'
  computedMethodology?: string | null
  recipeRationale?: string
}

export type OnOpenNote = (
  cellKey: string,
  position: { x: number; y: number },
  context: string,
  options?: OpenNoteOptions,
) => void

function LabelCell({
  label,
  indent,
  className = '',
  context,
  onOpenNote,
  cellKey,
  computedMethodology,
  recipeRationale,
}: {
  label: string
  indent: number
  className?: string
  context?: string
  cellKey?: string
  onOpenNote?: OnOpenNote
  canEdit?: boolean
  computedMethodology?: string | null
  recipeRationale?: string
}) {
  const handleContextMenu = (event: MouseEvent<HTMLTableCellElement>) => {
    if (!onOpenNote || !cellKey || !context) return
    event.preventDefault()
    onOpenNote(cellKey, { x: event.clientX, y: event.clientY }, context, {
      variant: 'line',
      computedMethodology,
      recipeRationale,
    })
  }

  return (
    <th
      scope="row"
      className={`relative !px-2 !py-1 !align-middle !text-left !normal-case !tracking-normal ${STICKY_LABEL} ${className}`}
      onContextMenu={handleContextMenu}
    >
      <span className="block truncate" style={indentStyle(indent)} title={label}>
        {label}
      </span>
    </th>
  )
}

function DriverCell({
  children,
  className = '',
  driverLeft,
}: {
  children?: React.ReactNode
  className?: string
  driverLeft: number
}) {
  return (
    <td className={`${STICKY_DRIVER} ${className}`} style={{ left: driverLeft }}>
      {children}
    </td>
  )
}

function DriverText({
  text,
  help,
  muted = false,
}: {
  text: string
  help?: string
  muted?: boolean
}) {
  const label = (
    <span
      className={`block truncate text-xs ${muted ? 'text-gray-600' : 'text-gray-500'}`}
      title={text}
    >
      {text}
    </span>
  )
  if (help) {
    return (
      <span className={BARE_TRIGGER}>
        <HoverHelp label={label} aria-label={text}>
          {help}
        </HoverHelp>
      </span>
    )
  }
  return label
}

function CarryForwardDriverSelect() {
  return (
    <SearchableSelect
      options={[{ id: 'carry_forward', label: DRIVER_LABELS.carry_forward }]}
      value="carry_forward"
      disabled
      placeholder="Driver"
      size="compact"
      preserveOrder
      allowClear={false}
      useViewportForPlacement
      className={LOCKED_DRIVER_SELECT}
      onChange={() => {}}
    />
  )
}

function getLastActualValue(
  history: Pick<History, 'nHistory' | 'values' | 'laborMeta'> | null,
  view: View,
  lineKey: string,
): number | null {
  if (!history || history.nHistory <= 0) {
    return null
  }
  const series = history.values[view]?.[lineKey]
  const raw = series?.[history.nHistory - 1]
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

function computeDriverSubline(
  lineState: { by_type: CaseInputs['drivers'][View][string]['by_type'] } | undefined,
  active: PickerDriver | null,
  lastClosedMonth: string,
): string | null {
  let shapeSubline: string | null = null
  try {
    const driverValues36 = active ? lineState?.by_type[active]?.values ?? null : null
    shapeSubline =
      active &&
      active !== 'ignore' &&
      active !== 'labor_buildup' &&
      active !== 'carry_forward' &&
      driverValues36
        ? detectShape(driverValues36, active, 36, {
            lastClosedMonth,
            unit: driverInputUnit(active),
          }).summary_text
        : null
  } catch {
    return null
  }
  try {
    const activeRecipe = active ? lineState?.by_type[active]?.recipe : null
    const fromRecipe =
      activeRecipe && active
        ? recipeSubline(activeRecipe, {
            lastClosedMonth,
            driverType: active,
            unit: driverInputUnit(active),
          })
        : null
    return fromRecipe || shapeSubline
  } catch {
    return shapeSubline
  }
}

function driverNoteIconProps({
  view,
  lineKey,
  lineLabel,
  notes,
  canEdit,
  onOpenNote,
  recipeRationale,
  computedMethodology,
}: {
  view: View
  lineKey: string
  lineLabel: string
  notes: Record<string, string>
  canEdit: boolean
  onOpenNote?: OnOpenNote
  recipeRationale?: string
  computedMethodology?: string | null
}) {
  const labelKey = labelCellKey(view, lineKey)
  const methodologyKey = methodologyCellKey(view, lineKey)
  return {
    context: lineLabel,
    note: notes[labelKey],
    methodology: notes[methodologyKey],
    recipeRationale,
    computedMethodology,
    canEdit,
    onClick: onOpenNote
      ? (position: { x: number; y: number }) =>
          onOpenNote(labelKey, position, lineLabel, {
            variant: 'line',
            computedMethodology,
            recipeRationale,
          })
      : undefined,
  }
}

type DriverColumnProps = {
  lineKey: string
  lineLabel: string
  valueUnit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct'
  view: View
  schema: SchemaEntry[]
  schemaByKey: Map<string, SchemaEntry>
  draft: CaseInputs
  history: Pick<History, 'nHistory' | 'values' | 'laborMeta'> | null
  canEdit: boolean
  driverLeft: number
  className?: string
  notes: Record<string, string>
  onOpenNote?: OnOpenNote
  onSetDriverType: (type: PickerDriverType) => void
  onSetDriverParams: (type: PickerDriver, params: DriverParams) => void
  onSetPctRef: (refKey: string) => void
  onClearDriverValues: () => void
  onToggleConsolidatedOnly?: () => void
  showConsolidatedCheckbox?: boolean
  locked?: boolean
  lastClosedMonth: string
}

function DriverColumn({
  lineKey,
  lineLabel,
  valueUnit,
  view,
  schema,
  schemaByKey,
  draft,
  history,
  canEdit,
  driverLeft,
  className = '',
  onSetDriverType,
  onSetDriverParams,
  onSetPctRef,
  onClearDriverValues,
  onToggleConsolidatedOnly,
  showConsolidatedCheckbox = false,
  locked = false,
  lastClosedMonth,
  notes,
  onOpenNote,
}: DriverColumnProps) {
  const [scurveOpen, setScurveOpen] = useState(false)
  const [pendingScurve, setPendingScurve] = useState(false)
  const [payDatesOpen, setPayDatesOpen] = useState(false)
  const [pendingPayPeriod, setPendingPayPeriod] = useState(false)
  const [pendingPayPeriodDriver, setPendingPayPeriodDriver] = useState<PickerDriver | null>(null)
  const payDatesAnchorRef = useRef<HTMLDivElement>(null)
  const lineState = draft.drivers[view]?.[lineKey]
  const active = (lineState?.active ?? null) as PickerDriver | null
  const allowed = orderedPickerDrivers(
    allowedDrivers(schema, lineKey, view, draft.consolidated_only, draft),
  )
  const refDriver =
    active === 'pct_of_line' || active === 'dollar_per_line_unit' ? active : null
  const pctRef = refDriver ? (lineState?.by_type[refDriver]?.params?.ref ?? '') : ''
  const refKeys = useMemo(() => {
    if (!refDriver) {
      return []
    }
    return selectableRefs(schema, draft, view, lineKey)
  }, [refDriver, draft, lineKey, schema, view])
  const scurveParams = lineState?.by_type.s_curve?.params
  const payPeriodParams = payPeriodParamsFromDriver(
    lineState?.by_type.dollar_per_pay_period?.params ??
      lineState?.by_type.labor_buildup?.params,
  )
  const defaultStart = getLastActualValue(history, view, lineKey)

  const activeRecipe = active ? lineState?.by_type[active]?.recipe : null
  const driverSubline = computeDriverSubline(lineState, active, lastClosedMonth)
  const noteIconProps = driverNoteIconProps({
    view,
    lineKey,
    lineLabel,
    notes,
    canEdit,
    onOpenNote,
    recipeRationale: activeRecipe?.rationale?.trim(),
    computedMethodology: driverSubline,
  })

  const openScurveModal = useCallback(() => {
    setScurveOpen(true)
  }, [])

  const openPayDatesPopover = useCallback(() => {
    setPayDatesOpen(true)
  }, [])

  const closePayDatesPopover = useCallback(() => {
    setPayDatesOpen(false)
    setPendingPayPeriod(false)
    setPendingPayPeriodDriver(null)
  }, [])

  const driverOptions = useMemo<SelectOption[]>(
    () => allowed.map((driver) => ({ id: driver, label: DRIVER_LABELS[driver] })),
    [allowed],
  )
  const refOptions = useMemo<SelectOption[]>(
    () =>
      refKeys.map((refKey) => ({
        id: refKey,
        label: schemaByKey.get(refKey)?.label ?? refKey,
      })),
    [refKeys, schemaByKey],
  )

  const handleDriverChange = (val: string | null) => {
    if (val === null) {
      if (!active) {
        return
      }
      if (
        !window.confirm(
          `Clear all projection driver values for "${lineLabel}"? The selected driver will remain.`,
        )
      ) {
        return
      }
      onClearDriverValues()
      return
    }
    const type = val as PickerDriver
    if (type === 's_curve' && !lineState?.by_type.s_curve?.params) {
      setPendingScurve(true)
      openScurveModal()
      return
    }
    if (
      (type === 'dollar_per_pay_period' || type === 'labor_buildup') &&
      !payPeriodParams
    ) {
      setPendingPayPeriod(true)
      setPendingPayPeriodDriver(type)
      openPayDatesPopover()
      return
    }
    setPendingScurve(false)
    setPendingPayPeriod(false)
    setPendingPayPeriodDriver(null)
    onSetDriverType(type)
  }

  const closeScurveModal = () => {
    setScurveOpen(false)
    setPendingScurve(false)
  }

  const selectedDriver = pendingScurve
    ? 's_curve'
    : pendingPayPeriod && pendingPayPeriodDriver
      ? pendingPayPeriodDriver
      : active

  if (locked) {
    return (
      <DriverCell className={className} driverLeft={driverLeft}>
        <div className="flex max-h-[54px] flex-col justify-center overflow-hidden">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <CarryForwardDriverSelect />
            </div>
            <DriverNoteIcon {...noteIconProps} />
          </div>
        </div>
      </DriverCell>
    )
  }

  return (
    <DriverCell className={className} driverLeft={driverLeft}>
      <div
        ref={payDatesAnchorRef}
        className="flex max-h-[54px] flex-col justify-center overflow-hidden"
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <SearchableSelect
              options={driverOptions}
              value={selectedDriver}
              disabled={!canEdit}
              placeholder="Driver"
              size="compact"
              preserveOrder
              allowClear
              useViewportForPlacement
              className="w-full min-w-0"
              onChange={(id) => handleDriverChange(id)}
            />
          </div>
          <DriverNoteIcon {...noteIconProps} />
        </div>
        <div className="flex min-w-0 flex-col justify-center gap-0 overflow-hidden">
        {refDriver ? (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <SearchableSelect
                options={refOptions}
                value={pctRef || null}
                disabled={!canEdit}
                placeholder="Reference line"
                size="compact"
                preserveOrder
                useViewportForPlacement
                className="w-full min-w-0"
                onChange={(id) => onSetPctRef(id ?? '')}
              />
            </div>
            <div className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
          </div>
        ) : null}
        {active === 's_curve' && canEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={openScurveModal}
          >
            Edit curve…
          </Button>
        ) : null}
        {(active === 'dollar_per_pay_period' || active === 'labor_buildup') && canEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={openPayDatesPopover}
          >
            Pay dates…
          </Button>
        ) : null}
        {showConsolidatedCheckbox ? (
          <label className="flex items-center gap-1 text-[10px] text-gray-600">
            <input
              type="checkbox"
              checked={draft.consolidated_only.includes(lineKey)}
              disabled={!canEdit}
              onChange={onToggleConsolidatedOnly}
            />
            Consolidated only
          </label>
        ) : null}
        </div>
      </div>
      {scurveOpen ? (
        <SCurveModal
          lineLabel={lineLabel}
          valueUnit={valueUnit}
          initialParams={{
            start: scurveParams?.start ?? defaultStart ?? 0,
            end: scurveParams?.end ?? defaultStart ?? 0,
            midpoint: scurveParams?.midpoint ?? 18,
            steepness: scurveParams?.steepness ?? 2,
          }}
          onCancel={closeScurveModal}
          onConfirm={(params) => {
            onSetDriverParams('s_curve', params)
            closeScurveModal()
          }}
        />
      ) : null}
      {payDatesOpen ? (
        <PayDatesPopover
          lineLabel={lineLabel}
          lastClosedMonth={lastClosedMonth}
          initialParams={payPeriodParams}
          anchorRef={payDatesAnchorRef}
          onCancel={closePayDatesPopover}
          onConfirm={(params) => {
            const driverType =
              pendingPayPeriodDriver ??
              (active === 'labor_buildup' ? 'labor_buildup' : 'dollar_per_pay_period')
            onSetDriverParams(driverType, params)
            closePayDatesPopover()
          }}
        />
      ) : null}
    </DriverCell>
  )
}

export type GridRowProps = {
  row: GridRowModel
  view: View
  schema: SchemaEntry[]
  schemaByKey: Map<string, SchemaEntry>
  draft: CaseInputs
  evaluated: Model | null
  histTs: number[]
  projTs: number[]
  history: Pick<History, 'nHistory' | 'values' | 'laborMeta'> | null
  lastClosedMonth: string
  canEdit: boolean
  driverLeft: number
  histMonthCount: number
  projMonthCount: number
  onSetDriverType: (line: string, type: PickerDriverType) => void
  onSetDriverParams: (line: string, type: PickerDriver, params: DriverParams) => void
  onSetPctRef: (line: string, refKey: string) => void
  onSetValue: (line: string, t: number, value: number | null) => void
  onApplyFromColumn: (line: string, startT: number, mode?: 'keep_shape' | 'replace_flat') => void
  onClearDriverValues: (line: string) => void
  onToggleConsolidatedOnly: (line: string) => void
  notes?: Record<string, string>
  onOpenNote?: OnOpenNote
  onOpenExplain?: (lineKey: string, t: number, anchor: DOMRect | { x: number; y: number }) => void
  collapsedSections?: ReadonlySet<string>
  onToggleSection?: (sectionKey: string) => void
  partnerLabels?: Record<string, string> | null
  compare?: ProjectionsGridCompare | null
  compareCellsByT?: Map<number, Record<string, CompareCell>>
  'data-index'?: number
}

function resolvedRowLabel(
  row: GridRowModel,
  partnerLabels: Record<string, string> | null | undefined,
): string {
  if (!('label' in row)) {
    return ''
  }
  if (!('key' in row)) {
    return row.label
  }
  return resolvePartnerLineLabel(row.label, row.key, partnerLabels)
}

function CashTieStatusCell({ balanced }: { balanced: boolean | null }) {
  if (balanced == null) {
    return <span className="text-sm text-gray-500">-</span>
  }
  return (
    <span className={balanced ? GREEN_STATUS_TAG : RED_STATUS_TAG}>
      {balanced ? 'Balanced' : 'ERROR'}
    </span>
  )
}

function getValue(
  evaluated: Model | null,
  history: GridRowProps['history'],
  view: View,
  key: string,
  t: number,
): number | string | null {
  if (t <= 0 && history) {
    const idx = history.nHistory - 1 + t
    const series = history.values[view]?.[key]
    if (series && idx >= 0 && idx < series.length) {
      return series[idx] ?? null
    }
  }
  return evaluated?.values[view]?.[key]?.[t] ?? null
}

function getDriverInput(
  draft: CaseInputs,
  view: View,
  lineKey: string,
  t: number,
): number | null {
  const lineState = draft.drivers[view]?.[lineKey]
  const active = lineState?.active
  if (!active) return null
  const values = lineState.by_type[active]?.values
  if (!values || t < 1 || t > 36) return null
  return values[t - 1] ?? null
}

function buildCompareCellHover(
  compare: ProjectionsGridCompare,
  cell: CompareCell,
  monthKey: string,
  valueUnit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct' | 'text',
): string {
  const actual = formatByUnit(valueUnit, cell.actual)
  const projected = formatByUnit(valueUnit, cell.projected)
  const deltaStr = formatCompareDelta(valueUnit, cell.delta)
  let deltaPart = `Δ ${deltaStr}`
  if (cell.deltaPct != null && Number.isFinite(cell.deltaPct)) {
    const pct =
      cell.deltaPct > 0
        ? `(+${formatPercent(cell.deltaPct).replace(/^\(|\)$/g, '')})`
        : ` ${formatPercent(cell.deltaPct)}`
    deltaPart = `${deltaPart}${pct}`
  }
  const source =
    compare.sourceByMonth[monthKey] ??
    ({
      caseSavedAt: compare.model.source.caseSavedAt,
      capturedAt: compare.model.source.capturedAt,
    } as const)
  const saved = formatDate(source.caseSavedAt)
  const captured = formatDate(source.capturedAt)
  const active = cell.driver?.active
  const driverLabel =
    active != null && active in DRIVER_LABELS
      ? DRIVER_LABELS[active as PickerDriver]
      : (active ?? 'none')
  let driverTail = driverLabel
  const driverVal = cell.driver?.value
  if (driverVal != null && Number.isFinite(driverVal) && active != null) {
    const unit =
      active in DRIVER_LABELS ? driverInputUnit(active as PickerDriver) : ('usd' as const)
    driverTail = `${driverLabel} ${formatByUnit(unit, driverVal)}`
  }
  return `Actual ${actual} · Projected ${projected} · ${deltaPart} · ${compare.caseName}, saved ${saved}, captured ${captured} · driver at capture: ${driverTail}`
}

const GridRow = forwardRef<HTMLTableRowElement, GridRowProps>(function GridRow(props, ref) {
  const {
    row,
    view,
    schema,
    schemaByKey,
    draft,
    evaluated,
    histTs,
    projTs,
    history,
    lastClosedMonth,
    canEdit,
    driverLeft,
    histMonthCount,
    projMonthCount,
    onSetDriverType,
    onSetDriverParams,
    onSetPctRef,
    onSetValue,
    onApplyFromColumn,
    onClearDriverValues,
    onToggleConsolidatedOnly,
    notes = {},
    onOpenNote,
    onOpenExplain,
    collapsedSections = new Set(),
    onToggleSection,
    partnerLabels = null,
    compare = null,
    compareCellsByT,
    'data-index': dataIndex,
  } = props

  const rowLabel = resolvedRowLabel(row, partnerLabels)
  const inputLineKey = row.kind === 'input' ? row.key : ''

  const labelNoteProps = (
    lineKey: string,
    lineLabel: string,
    computedMethodology?: string | null,
    recipeRationale?: string,
  ) => {
    const cellKey = labelCellKey(view, lineKey)
    return {
      cellKey,
      context: lineLabel,
      onOpenNote,
      canEdit,
      computedMethodology,
      recipeRationale,
    }
  }

  const oneTimeByMonth = useMemo(() => {
    if (row.kind !== 'input') {
      return new Map<string, typeof draft.one_time_items>()
    }
    const map = new Map<string, typeof draft.one_time_items>()
    for (const item of draft.one_time_items) {
      if (item.view !== view || item.line_key !== inputLineKey) {
        continue
      }
      const existing = map.get(item.month) ?? []
      existing.push(item)
      map.set(item.month, existing)
    }
    const policy = draft.cash_policy
    const yieldView = policy?.enabled ? (policy.sweep.yield_income_view ?? 'ga') : null
    if (
      yieldView &&
      view === yieldView &&
      inputLineKey === 'is.plus_interest_income' &&
      evaluated?.cash_policy
    ) {
      for (const month of evaluated.cash_policy.months) {
        if (!month.interest || month.interest <= 0) {
          continue
        }
        const existing = map.get(month.month) ?? []
        existing.push({
          id: `policy:interest:${month.t}`,
          month: month.month,
          view: yieldView,
          line_key: 'is.plus_interest_income',
          amount: month.interest,
          mode: 'incremental',
          notes: `+ ${formatCurrency(month.interest)} money market yield (cash policy)`,
        })
        map.set(month.month, existing)
      }
    }
    return map
  }, [draft, evaluated?.cash_policy, inputLineKey, row.kind, view])

  const handleTabNext = (lineKey: string, currentT: number) => {
    const idx = projTs.indexOf(currentT)
    if (idx >= 0 && idx < projTs.length - 1) {
      const nextT = projTs[idx + 1]
      const nextInput = document.querySelector<HTMLInputElement>(
        `input[data-proj-input="${lineKey}-${nextT}"]`,
      )
      nextInput?.focus()
    }
  }

  if (row.kind === 'band') {
    const collapsed = collapsedSections.has(row.sectionKey)
    return (
      <tr ref={ref} data-index={dataIndex} className={`${ROW} ${SECTION_BAND}`}>
        <td
          colSpan={2}
          className={`${SECTION_BAND} table-locked-edge sticky left-0 z-20 !px-2 !py-0 align-middle text-[11px] font-semibold uppercase tracking-wider !text-white`}
          onClick={() => onToggleSection?.(row.sectionKey)}
        >
          <span className="flex items-center gap-1.5">
            <ChevronDownIcon
              className={`h-3.5 w-3.5 shrink-0 text-white transition-transform ${collapsed ? '-rotate-90' : ''}`}
              aria-hidden
            />
            <span className="truncate">{row.label}</span>
          </span>
        </td>
        <td
          colSpan={histMonthCount}
          className={SECTION_BAND}
          onClick={() => onToggleSection?.(row.sectionKey)}
        />
        <td
          colSpan={projMonthCount}
          className={`${SECTION_BAND} ${PROJ_EDGE}`}
          onClick={() => onToggleSection?.(row.sectionKey)}
        />
      </tr>
    )
  }

  if (row.kind === 'sectionPad') {
    return (
      <tr ref={ref} data-index={dataIndex} className={`h-5 max-h-5 ${SPACER_ROW}`}>
        <td className="sticky left-0 z-20 !p-0" />
        <td className={`${STICKY_DRIVER} !p-0`} style={{ left: driverLeft }} />
        <td colSpan={histMonthCount} className={`!p-0 ${HIST_COL}`} />
        <td colSpan={projMonthCount} className={`!p-0 ${PROJ_EDGE}`} />
      </tr>
    )
  }

  if (row.kind === 'gap') {
    return (
      <tr ref={ref} data-index={dataIndex} className={`h-4 max-h-4 ${SPACER_ROW} proj-grid-gap`}>
        <td className="sticky left-0 z-20 bg-white !p-0" />
        <td className={`${STICKY_DRIVER} bg-white !p-0`} style={{ left: driverLeft }} />
        <td colSpan={histMonthCount} className={`!p-0 ${HIST_COL}`} />
        <td colSpan={projMonthCount} className={`!p-0 ${PROJ_EDGE}`} />
      </tr>
    )
  }

  if (row.kind === 'group') {
    const isGrey = row.tone === 'grey'
    const headerClass = isGrey ? GROUP_HEADER_GREY : GROUP_HEADER
    const stickyLabel = isGrey ? GROUP_STICKY_LABEL_GREY : GROUP_STICKY_LABEL
    const stickyDriver = isGrey ? GROUP_STICKY_DRIVER_GREY : GROUP_STICKY_DRIVER
    const labelTextClass = isGrey ? 'text-gray-800' : 'text-green-800'
    return (
      <tr ref={ref} data-index={dataIndex} className={GROUP_ROW}>
        <td className={`${stickyLabel} ${headerClass}`}>
          <span
            className={`block truncate text-sm font-semibold ${labelTextClass}`}
            style={indentStyle(row.indent)}
          >
            {row.label}
          </span>
        </td>
        <td className={`${stickyDriver} ${headerClass}`} style={{ left: driverLeft }} />
        {histTs.map((t) => (
          <td key={`h-${t}`} className={headerClass} />
        ))}
        {projTs.map((t, i) => (
          <td key={`p-${t}`} className={`${headerClass} ${i === 0 ? PROJ_EDGE : ''}`} />
        ))}
      </tr>
    )
  }

  if (row.kind === 'text') {
    const labelProps = labelNoteProps(row.key, rowLabel)
    return (
      <tr ref={ref} data-index={dataIndex} className={ROW}>
        <LabelCell
          label={rowLabel}
          indent={row.indent}
          className="!text-sm !font-normal !text-gray-700"
          {...labelProps}
        />
        <DriverCell driverLeft={driverLeft}>
          <div className="flex max-h-[54px] flex-col justify-center overflow-hidden">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <CarryForwardDriverSelect />
              </div>
              <DriverNoteIcon
                {...driverNoteIconProps({
                  view,
                  lineKey: row.key,
                  lineLabel: rowLabel,
                  notes,
                  canEdit,
                  onOpenNote,
                })}
              />
            </div>
          </div>
        </DriverCell>
        {histTs.map((t) => (
          <HistoryValueCell
            key={`h-${t}`}
            value={getValue(evaluated, history, view, row.key, t)}
            unit="text"
          />
        ))}
        {projTs.map((t, i) => (
          <ReadOnlyValueCell
            key={`p-${t}`}
            value={getValue(evaluated, history, view, row.key, t)}
            unit="text"
            isFirstProjected={i === 0}
          />
        ))}
      </tr>
    )
  }

  if (row.kind === 'computed') {
    const { trClass, cellRule, labelClass, valueClass } = computedRowClasses(row)
    const labelProps = labelNoteProps(row.key, rowLabel)
    const laborBuildupWages = isLaborBuildupWagesRow(view, row.key, draft)
    const firstProjT = projTs[0] ?? 1
    const laborDriverHelp = laborBuildupWages
      ? buildLaborBuildupHelp({
          view,
          t: firstProjT,
          lastClosedMonth,
          draft,
          evaluated,
          history,
          includeResidual: true,
        })
      : row.driverHelp
    return (
      <tr ref={ref} data-index={dataIndex} className={`${ROW} ${trClass}`}>
        <LabelCell
          label={rowLabel}
          indent={row.indent}
          className={`${WEIGHT_LABEL[row.weight]} ${labelClass} ${cellRule}`}
          {...labelProps}
        />
        <DriverCell className={cellRule} driverLeft={driverLeft}>
          <div className="flex max-h-[54px] flex-col justify-center overflow-hidden">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                {row.driverLabel ? (
                  <DriverText text={row.driverLabel} help={laborDriverHelp ?? row.driverHelp} />
                ) : null}
              </div>
              <DriverNoteIcon
                {...driverNoteIconProps({
                  view,
                  lineKey: row.key,
                  lineLabel: rowLabel,
                  notes,
                  canEdit,
                  onOpenNote,
                })}
              />
            </div>
          </div>
        </DriverCell>
        {histTs.map((t) => (
          <td key={`h-${t}`} className={`${HIST_COL} ${WEIGHT_VALUE[row.weight]} ${valueClass} ${cellRule} truncate`}>
            <div className="flex justify-end">
              <span className="truncate">
                {formatByUnit(row.unit, getValue(evaluated, history, view, row.key, t) as number | null)}
              </span>
            </div>
          </td>
        ))}
        {projTs.map((t, i) => {
          const cellValue = getValue(evaluated, history, view, row.key, t) as number | null
          const formatted = formatByUnit(row.unit, cellValue)
          const cellHelp =
            laborBuildupWages && cellValue != null
              ? buildLaborBuildupHelp({
                  view,
                  t,
                  lastClosedMonth,
                  draft,
                  evaluated,
                  history,
                  includeResidual: false,
                })
              : null
          const monthKey = monthKeyFromT(lastClosedMonth, t)
          const openExplain = laborBuildupWages && onOpenExplain
            ? (anchor: DOMRect | { x: number; y: number }) => onOpenExplain(row.key, t, anchor)
            : undefined
          return (
            <td
              key={`p-${t}`}
              className={`group relative ${WEIGHT_VALUE[row.weight]} ${valueClass} ${cellRule} truncate ${i === 0 ? PROJ_EDGE : ''}`}
              onMouseDown={
                openExplain
                  ? (event) => {
                      if (!event.altKey) return
                      event.preventDefault()
                      openExplain({ x: event.clientX, y: event.clientY })
                    }
                  : undefined
              }
            >
              {openExplain ? (
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-0 top-0 hidden text-[10px] font-semibold text-gray-500 group-hover:inline group-focus-within:inline"
                  aria-label={`Explain ${rowLabel}, ${monthKey}`}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    openExplain(event.currentTarget.getBoundingClientRect())
                  }}
                >
                  i
                </button>
              ) : null}
              <div className="flex justify-end">
                {cellHelp ? (
                  <span className={BARE_TRIGGER}>
                    <HoverHelp label={<span className="truncate">{formatted}</span>} aria-label={formatted}>
                      {cellHelp}
                    </HoverHelp>
                  </span>
                ) : (
                  <span className="truncate">{formatted}</span>
                )}
              </div>
            </td>
          )
        })}
      </tr>
    )
  }

  if (row.kind === 'cf_tie_check') {
    const renderStatusCell = (t: number, isFirstProjected: boolean) => {
      const { eop, bsCash } = cashTieValues(evaluated, history, view, t)
      const balanced = cashTieBalanced(eop, bsCash)
      return (
        <td
          key={`${t < 1 ? 'h' : 'p'}-${t}`}
          className={`${VALUE} ${t < 1 ? HIST_COL : ''} ${isFirstProjected ? PROJ_EDGE : ''}`}
        >
          <div className="flex justify-end">
            <CashTieStatusCell balanced={balanced} />
          </div>
        </td>
      )
    }
    return (
      <tr ref={ref} data-index={dataIndex} className={ROW}>
        <LabelCell label={rowLabel} indent={row.indent} className="!text-sm !font-normal !text-gray-700" />
        <DriverCell driverLeft={driverLeft} />
        {histTs.map((t) => renderStatusCell(t, false))}
        {projTs.map((t, i) => renderStatusCell(t, i === 0))}
      </tr>
    )
  }

  if (row.kind === 'greyed') {
    const wholeRow = row.scope === 'row'
    const projectedOnly = row.scope === 'projected'
    const labelProps = labelNoteProps(row.key, rowLabel)
    return (
      <tr ref={ref} data-index={dataIndex} className={ROW}>
        <LabelCell
          label={rowLabel}
          indent={row.indent}
          className={
            wholeRow ? `!text-sm !font-normal ${GREYED}` : '!text-sm !font-normal !text-gray-700'
          }
          {...labelProps}
        />
        <DriverCell className={wholeRow ? GREYED : ''} driverLeft={driverLeft}>
          <div className="flex max-h-[54px] flex-col justify-center overflow-hidden">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <DriverText text={row.driverLabel} muted={wholeRow} />
              </div>
              <DriverNoteIcon
                {...driverNoteIconProps({
                  view,
                  lineKey: row.key,
                  lineLabel: rowLabel,
                  notes,
                  canEdit,
                  onOpenNote,
                })}
              />
            </div>
          </div>
        </DriverCell>
        {histTs.map((t) => (
          <td key={`h-${t}`} className={wholeRow ? `${VALUE} ${GREYED}` : `${VALUE} ${HIST_COL}`}>
            <div className="flex justify-end">
              <span className="truncate">
                {formatByUnit(row.unit, getValue(evaluated, history, view, row.key, t) as number | null)}
              </span>
            </div>
          </td>
        ))}
        {projTs.map((t, i) => (
          <td
            key={`p-${t}`}
            className={`${VALUE} ${GREYED} ${i === 0 ? PROJ_EDGE : ''}`}
          >
            <div className="flex justify-end">
              <span className="truncate">
                {projectedOnly
                  ? '-'
                  : formatByUnit(
                      row.unit,
                      getValue(evaluated, history, view, row.key, t) as number | null,
                    )}
              </span>
            </div>
          </td>
        ))}
      </tr>
    )
  }

  const lineState = draft.drivers[view]?.[row.key]
  const active = (lineState?.active ?? null) as PickerDriver | null
  const driverTypeForRow = row.driverLocked ? 'carry_forward' : active

  const invertCtx: DriverContext = {
    getNumeric: (v, key, monthT) => {
      const val = getValue(evaluated, history, v, key, monthT)
      return typeof val === 'number' ? val : null
    },
    daysInMonth: (monthT) => daysInMonth(lastClosedMonth, monthT),
    monthKey: (monthT) => monthKeyFromT(lastClosedMonth, monthT),
    driverParams: () => (active ? lineState?.by_type[active]?.params : undefined),
    driverInput: () => null,
    addDiagnostic: () => {},
    schemaByKey: {},
    view,
  }

  const activeRecipeForLabel = active ? lineState?.by_type[active]?.recipe : null
  const computedMethodology = computeDriverSubline(lineState, active, lastClosedMonth)
  const labelProps = labelNoteProps(
    row.key,
    rowLabel,
    computedMethodology,
    activeRecipeForLabel?.rationale?.trim(),
  )
  const sectionStartRule = SECTION_START_KEYS.has(row.key) ? RULE_SUBTOTAL : ''

  return (
    <tr ref={ref} data-index={dataIndex} className={INPUT_ROW}>
      <LabelCell
        label={rowLabel}
        indent={row.indent}
        className="!text-sm !font-normal !text-gray-700"
        {...labelProps}
      />
      <DriverColumn
        lineKey={row.key}
        lineLabel={rowLabel}
        valueUnit={row.valueUnit}
        view={view}
        schema={schema}
        schemaByKey={schemaByKey}
        draft={draft}
        history={history}
        canEdit={canEdit}
        driverLeft={driverLeft}
        locked={row.driverLocked}
        notes={notes}
        onOpenNote={onOpenNote}
        onSetDriverType={(type) => onSetDriverType(row.key, type)}
        onSetDriverParams={(type, params) => onSetDriverParams(row.key, type, params)}
        onSetPctRef={(ref) => onSetPctRef(row.key, ref)}
        onClearDriverValues={() => onClearDriverValues(row.key)}
        onToggleConsolidatedOnly={() => onToggleConsolidatedOnly(row.key)}
        showConsolidatedCheckbox={view === 'consolidated' && row.key.startsWith('is.')}
        lastClosedMonth={lastClosedMonth}
      />
      {histTs.map((t) => {
        const monthKey = monthKeyFromT(lastClosedMonth, t)
        const cellKey = historicalCellKey(view, row.key, t)
        const cellContext = `${rowLabel}, ${monthKey}`
        const cellsForT = compareCellsByT?.get(t)
        const compareCell = cellsForT?.[row.key]
        const pinned =
          compare != null && (compare.columns === 'all' || compare.columns.includes(monthKey))
        const showCompare = Boolean(compare && pinned && compareCell)
        const compareProp = showCompare
          ? {
              delta: compareCell!.delta,
              deltaPct: compareCell!.deltaPct,
              projected: compareCell!.projected,
              hover: buildCompareCellHover(compare!, compareCell!, monthKey, row.valueUnit),
            }
          : undefined
        return (
          <HistoryValueCell
            key={`h-${t}`}
            value={getValue(evaluated, history, view, row.key, t)}
            unit={row.valueUnit}
            isInputRow
            driverType={driverTypeForRow}
            impliedDriver={
              driverTypeForRow
                ? impliedDriverInput(driverTypeForRow, invertCtx, view, row.key, t)
                : null
            }
            cellRule={sectionStartRule}
            cellKey={cellKey}
            note={notes[cellKey]}
            context={cellContext}
            canEdit={canEdit}
            onOpenNote={onOpenNote}
            compare={compareProp}
          />
        )
      })}
      {projTs.map((t, i) => {
        const monthKey = monthKeyFromT(lastClosedMonth, t)
        const payPeriodParamsForRow = payPeriodParamsFromDriver(
          lineState?.by_type.dollar_per_pay_period?.params,
        )
        const payDateCount =
          active === 'dollar_per_pay_period' && payPeriodParamsForRow
            ? distributionsInMonth(
                payPeriodParamsForRow.cadence,
                payPeriodParamsForRow.anchor_date,
                monthKey,
              )
            : null
        const activeRecipeForRow = active ? lineState?.by_type[active]?.recipe : null
        const hasOverride = activeRecipeForRow?.overrides?.[monthKey] !== undefined
        const driverTypeForCell = row.driverLocked ? 'carry_forward' : active
        const driverInput = getDriverInput(draft, view, row.key, t)
        const computed = getValue(evaluated, history, view, row.key, t) as number | null
        const oneTimeItems = oneTimeByMonth.get(monthKey) ?? []
        const denomZero = isDenomZero(driverTypeForCell, driverInput, computed)
        const driverUnit =
          driverTypeForCell && driverTypeForCell !== 'carry_forward'
            ? driverInputUnit(driverTypeForCell)
            : 'usd'
        const hasEditableDriver =
          driverTypeForCell != null &&
          driverTypeForCell !== 's_curve' &&
          driverTypeForCell !== 'carry_forward' &&
          driverTypeForCell !== 'ignore'
        const identity =
          hasEditableDriver &&
          isIdentity(driverTypeForCell!, driverUnit, row.valueUnit, driverInput, computed, {
            hasOneTime: oneTimeItems.length > 0,
            denomZero,
          })
        const identityExplain =
          identity && onOpenExplain && history
            ? explainCell({
                view,
                lineKey: row.key,
                t,
                schema,
                inputs: draft,
                history: history as History,
                evaluated,
                notes,
                lastClosedMonth,
              })
            : null
        return (
          <GridCell
            key={`p-${t}`}
            lineKey={row.key}
            lineLabel={rowLabel}
            monthKey={monthKey}
            t={t}
            isFirstProjected={i === 0}
            driverType={driverTypeForCell}
            driverInput={driverInput}
            computed={computed}
            payDateCount={payDateCount}
            valueUnit={row.valueUnit}
            oneTimeItems={oneTimeItems}
            canEdit={canEdit}
            hasOverride={hasOverride}
            cellRule={sectionStartRule}
            cellKey={projectedCellKey(view, row.key, t)}
            note={notes[projectedCellKey(view, row.key, t)]}
            onOpenNote={onOpenNote}
            identityExplain={identityExplain}
            onOpenExplain={
              onOpenExplain && active && active !== 'ignore'
                ? (anchor) => onOpenExplain(row.key, t, anchor)
                : undefined
            }
            onCommit={(monthT, value) => onSetValue(row.key, monthT, value)}
            onTabNext={(monthT) => handleTabNext(row.key, monthT)}
            onApplyFromColumn={(startT, mode) => onApplyFromColumn(row.key, startT, mode)}
          />
        )
      })}
    </tr>
  )
})

export default memo(GridRow)
